import type { Slot, SlotTransform } from './types';
import { clamp, round } from './utils';

export const IDENTITY_TRANSFORM: SlotTransform = {
  scale: 1,
  x: 0,
  y: 0,
  rotation: 0,
  flipH: false,
  flipV: false,
};

export interface ImageSize {
  width: number;
  height: number;
}

export interface CellSize {
  w: number;
  h: number;
}

/**
 * Everything the framing math needs from a slot. Widened from `Slot` so the
 * splitter's source and the carousel's image layers — which carry the same two
 * fields — can be passed without a cast or a wrapper object.
 */
export type SlotLike = Pick<Slot, 'fit' | 'transform'>;

export interface DrawGeometry {
  /** on-screen size of the (unrotated) image */
  drawW: number;
  drawH: number;
  /** rotated bounding box */
  boxW: number;
  boxH: number;
  /** pan offset in px, already clamped */
  offX: number;
  offY: number;
  /** where the image centre lands inside the cell */
  cx: number;
  cy: number;
  /** scale factor that makes the image exactly fit the cell */
  fitScale: number;
  maxOffX: number;
  maxOffY: number;
}

function rotatedBox(img: ImageSize, rotationDeg: number) {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    w: img.width * cos + img.height * sin,
    h: img.width * sin + img.height * cos,
  };
}

/**
 * Turn a slot's normalised transform into concrete pixel geometry for a cell.
 * Preview and exporter both consume this so they can never drift apart.
 */
export function computeGeometry(img: ImageSize, cell: CellSize, slot: SlotLike): DrawGeometry {
  const t = slot.transform;
  const box = rotatedBox(img, t.rotation);
  const fitScale =
    slot.fit === 'cover'
      ? Math.max(cell.w / box.w, cell.h / box.h)
      : Math.min(cell.w / box.w, cell.h / box.h);

  const s = fitScale * Math.max(0.05, t.scale);
  const drawW = img.width * s;
  const drawH = img.height * s;
  const boxW = box.w * s;
  const boxH = box.h * s;

  const maxOffX = Math.abs(boxW - cell.w) / 2;
  const maxOffY = Math.abs(boxH - cell.h) / 2;

  const offX = clamp(t.x * cell.w, -maxOffX, maxOffX);
  const offY = clamp(t.y * cell.h, -maxOffY, maxOffY);

  return {
    drawW,
    drawH,
    boxW,
    boxH,
    offX,
    offY,
    cx: cell.w / 2 + offX,
    cy: cell.h / 2 + offY,
    fitScale,
    maxOffX,
    maxOffY,
  };
}

/** Clamp a slot's stored pan so it never reveals empty space in `cover` mode. */
export function clampPan(img: ImageSize, cell: CellSize, slot: SlotLike): { x: number; y: number } {
  const g = computeGeometry(img, cell, slot);
  return {
    x: cell.w > 0 ? round(g.offX / cell.w, 5) : 0,
    y: cell.h > 0 ? round(g.offY / cell.h, 5) : 0,
  };
}

/** CSS transform for the preview `<img>`; mirrors the canvas draw exactly. */
export function toCSSTransform(g: DrawGeometry, t: SlotTransform): string {
  const tx = g.cx - g.drawW / 2;
  const ty = g.cy - g.drawH / 2;
  const sx = t.flipH ? -1 : 1;
  const sy = t.flipV ? -1 : 1;
  return `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px) rotate(${t.rotation}deg) scale(${sx}, ${sy})`;
}

export function normaliseRotation(deg: number) {
  return ((deg % 360) + 360) % 360;
}
